<script setup>
import dayjs from 'dayjs'
import { computed, ref } from 'vue'
import relativeTime from 'dayjs/plugin/relativeTime'
import InlineEditField from '@/components/shared/InlineEditField.vue'
import useDateTimeFormatter from '@/composables/useDateTimeFormatter'

/**
 * The dayjs plugin.
 */
dayjs.extend(relativeTime)

/**
 * The component's composables.
 */
const { formatDateTime } = useDateTimeFormatter()

const props = defineProps({
    /**
     * The comment to displayed.
     *
     * @type {Object}
     */
    comment: {
        type: Object,
        required: true,
    },
})

/**
 * The local refs.
 */
const isEditing = ref(false)

/**
 * The component's emits.
 */
const emits = defineEmits(['edit', 'delete'])

/**
 * Get the full name of the user.
 *
 * @return {String}
 */
const fullName = computed(() => {
    const user = props.comment.user

    return user ? `${user.first_name} ${user.last_name}` : 'Unknown'
})

/**
 * Get the initials of the user.
 *
 * @return {String}
 */
const initials = computed(() => {
    const user = props.comment.user

    if (!user || !user?.first_name || !user?.last_name) {
        return 'U'
    }

    return `${user.first_name.charAt(0)}${user.last_name.charAt(0)}`
})

/**
 * The stop's late time.
 *
 * @return {String}
 */
const createdAt = computed(() => {
    const hoursOfDifference = dayjs().diff(
        dayjs(props.comment.created_at),
        'hour'
    )

    if (hoursOfDifference > 1) {
        return formatDateTime(
            props.comment.created_at,
            'MM/D/YY hh:mma'
        ).replace(/^0+/, '')
    }

    const timeDifference = dayjs().from(props.comment.created_at, 'day')

    return `${timeDifference} ago`
})

const onCancel = () => {
    isEditing.value = false
}

/**
 * Handle the delete click.
 *
 * @return {void}
 */
const onDeleteClick = () => {
    emits('delete', props.comment.id)
}

/**
 * Handle the edit click.
 *
 * @return {void}
 */
const onEditClick = () => {
    isEditing.value = true
}

/**
 * Handle the save event.
 *
 * @param {String} value
 * @return {void}
 */
const onSave = (value) => {
    isEditing.value = false

    emits('edit', {
        id: props.comment.id,
        comment: value,
        priority: props.comment.priority.id,
    })
}
</script>

<template>
    <div class="flex w-full border border-gray-200 p-3 rounded">
        <div class="flex mr-4">
            <v-avatar class="mr-2 bg-gray-600 text-white" size="x-small">
                <span>{{ initials }}</span>
            </v-avatar>
        </div>
        <div class="flex flex-col w-full">
            <div class="flex justify-between mb-2">
                <div class="flex items-center mb-1">
                    {{ fullName }}
                </div>

                <v-menu>
                    <template v-slot:activator="{ props }">
                        <v-icon
                            size="small"
                            icon="fas fa-ellipsis"
                            v-bind="props"
                        ></v-icon>
                    </template>

                    <v-list>
                        <v-list-item @click.prevent="onEditClick">
                            <v-list-item-title>Edit</v-list-item-title>
                        </v-list-item>
                        <v-list-item @click.prevent="onDeleteClick">
                            <v-list-item-title>Delete</v-list-item-title>
                        </v-list-item>
                    </v-list>
                </v-menu>
            </div>
            <inline-edit-field
                class=""
                :value="comment.comment"
                :is-editable="isEditing"
                @save="onSave"
                @cancel="onCancel"
            />

            <div class="flex justify-between">
                <div class="py-2 text-xs text-gray-500">{{ createdAt }}</div>
                <div>
                    <v-icon
                        class="text-xs"
                        :class="{
                            'text-red-600': comment.priority.id === 2,
                            'text-blue-600': comment.priority.id === 1,
                            'text-green-600': comment.priority.id === 0,
                        }"
                        icon="fas fa-circle"
                    ></v-icon>
                </div>
            </div>
        </div>
    </div>
</template>
